import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const adImg = (id) => `${API}/ads/image/${id}`;

export const adHref = (a) => (a.link_type === "url" ? a.link : a.link_type === "whatsapp" ? `https://wa.me/${String(a.link).replace(/\D/g, "").replace(/^0/, "966")}` : null);

export const AdSlide = ({ a, onClick, zoom = false }) => (
  <div className="relative h-full w-full cursor-pointer overflow-hidden" onClick={onClick}>
    <picture>
      {a.mobile_image_id && <source media="(max-width: 639px)" srcSet={adImg(a.mobile_image_id)} />}
      <img src={adImg(a.image_id)} alt={a.title || "إعلان"} className={`h-full w-full object-cover transition-transform ease-linear ${zoom ? "scale-105" : "scale-100"}`} style={{ transitionDuration: `${(a.duration || 5) * 1000}ms` }} draggable={false} />
    </picture>
    {(a.title || a.text || a.button) && (
      <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/70 via-black/20 to-transparent p-4 text-white sm:p-6" dir="rtl">
        {a.title && <h3 className="font-heading text-lg font-extrabold leading-tight sm:text-2xl">{a.title}</h3>}
        {a.text && <p className="mt-1 line-clamp-2 max-w-2xl text-xs text-white/85 sm:text-sm">{a.text}</p>}
        {a.button && a.link_type !== "none" && <span className="mt-2 inline-flex w-fit rounded-full bg-white px-4 py-1.5 text-xs font-bold text-slate-900 shadow">{a.button}</span>}
      </div>)}
  </div>
);

// Rotating ads under the dashboard hero (managed by the platform owner)
export const AdsCarousel = () => {
  const [ads, setAds] = useState([]);
  const [i, setI] = useState(0);
  const [pause, setPause] = useState(false);
  const touch = useRef(null);
  const seen = useRef(new Set());
  const box = useRef(null);
  useEffect(() => { axios.get(`${API}/ads`).then((r) => setAds(r.data || [])).catch(() => {}); }, []);
  const dur = (ads[i]?.duration || 5) * 1000;
  useEffect(() => {
    if (ads.length < 2 || pause) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % ads.length), dur);
    return () => clearTimeout(t);
  }, [ads.length, pause, i, dur]);
  useEffect(() => {
    const a = ads[i];
    if (!a || seen.current.has(a.id) || document.hidden) return undefined;
    const t = setTimeout(() => {
      const r = box.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) return;
      seen.current.add(a.id);
      axios.post(`${API}/ads/${a.id}/event`, { kind: "view" }).catch(() => {});
    }, 1000);
    return () => clearTimeout(t);
  }, [ads, i]);
  if (!ads.length) return null;
  const go = (d) => setI((x) => (x + d + ads.length) % ads.length);
  const open = (a) => {
    const h = adHref(a);
    if (h || (a.link_type === "section" && a.link)) axios.post(`${API}/ads/${a.id}/event`, { kind: "click" }).catch(() => {});
    if (h) window.open(h, "_blank", "noopener");
    else if (a.link_type === "section" && a.link) window.dispatchEvent(new CustomEvent("wz-goto", { detail: a.link }));
  };
  return (
    <div ref={box} className="group relative overflow-hidden rounded-3xl bg-slate-200 shadow-lg" data-testid="ads-carousel"
      onMouseEnter={() => setPause(true)} onMouseLeave={() => setPause(false)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 40) go(dx > 0 ? 1 : -1); touch.current = null; }}>
      <div className="relative aspect-[2/1] w-full overflow-hidden sm:aspect-[3/1]">
        <div className="flex h-full w-full transition-transform duration-700 ease-[cubic-bezier(.22,1,.36,1)]" dir="ltr" style={{ transform: `translateX(-${i * 100}%)` }}>
          {ads.map((a, k) => (
            <div key={a.id} className={`h-full w-full shrink-0 overflow-hidden ${k === i ? "" : "pointer-events-none"}`} data-testid={`ad-slide-${a.id}`}>
              <AdSlide a={a} onClick={() => open(a)} zoom={k === i && ads.length > 1 && !pause} />
            </div>))}
        </div>
      </div>
      {ads.length > 1 && <>
        <button onClick={() => go(-1)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 text-slate-800 opacity-0 shadow transition-opacity group-hover:opacity-100" data-testid="ads-prev"><ChevronRight className="h-4 w-4" /></button>
        <button onClick={() => go(1)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 text-slate-800 opacity-0 shadow transition-opacity group-hover:opacity-100" data-testid="ads-next"><ChevronLeft className="h-4 w-4" /></button>
      </>}
      {ads.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 bg-white/90 py-2" dir="ltr" data-testid="ads-dots">{ads.map((a, k) => (
          <button key={a.id} onClick={() => setI(k)} className={`relative h-1.5 overflow-hidden rounded-full bg-slate-300 transition-all ${k === i ? "w-8" : "w-1.5"}`} data-testid={`ads-dot-${k}`}>
            {k === i && <span key={`${i}-${pause}`} className="absolute inset-y-0 left-0 rounded-full bg-slate-700" style={{ animation: pause ? "none" : `wz-ad-prog ${dur}ms linear forwards`, width: pause ? "100%" : undefined }} />}
          </button>))}</div>
      )}
      {ads.length > 1 && <>
        <style>{"@keyframes wz-ad-prog{from{width:0}to{width:100%}}"}</style>
      </>}
    </div>
  );
};
